import {gql} from '@apollo/client';
import {Button, Col, notification, Row, Skeleton, Space, Tooltip} from 'antd';
import {find, memoize} from 'lodash';
import {useCallback, useMemo, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {Art, Artist, TransactionButton} from 'src/components';
import {ArtSize} from 'src/components/Art';
import {ArtistSize} from 'src/components/Artist';
import {useAddress, useGameContract, useQuery} from 'src/hooks';
import {useLisaMonaParameters} from 'src/hooks/useLisaMonaParameters';
import {ConditionalWrapper, getHybridId} from 'src/utils';
import {PlayerArts} from './__generated__/PlayerArts';

const GQL_PLAYER_ARTS = gql`
  query PlayerArts($playerId: ID!, $artId: ID!) {
    art(subgraphError: allow, id: $artId) {
      id
      uri # Needed for metadata augmentation
      imageUri
      realArtist
    }
    hybridArts: arts(subgraphError: allow, where: {isHybrid: true}) {
      id
    }
    player(subgraphError: allow, id: $playerId) {
      id
      arts(orderBy: mintedAt, orderDirection: desc, where: {mintedAt_gt: 0}) {
        id
        uri
        realArtist
        title
        mintedAt
        imageUri
        mintedAt
        artist {
          uri # Needed for metadata augmentation
          name
        }
      }
      artists(orderBy: mintedAt, orderDirection: desc) {
        id
        name
        uri # Needed for metadata augmentation
        imageUri
        creationReadyAt
      }
    }
  }
`;

export default function Create() {
  const {contentId} = useParams<{contentId: string}>();
  const history = useHistory();
  const address = useAddress();
  const contract = useGameContract();
  const {hybridEth} = useLisaMonaParameters();
  const [styleId, setStyleId] = useState<string>();
  const [artistId, setArtistId] = useState<string>();

  const {data, loading} = useQuery<PlayerArts>(GQL_PLAYER_ARTS, {
    variables: {
      artId: contentId,
      playerId: address,
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const isExistingHybrid = useMemo(
    () =>
      memoize(
        (_contentId: string, _styleId: string): boolean => {
          const hybridId = getHybridId(_contentId, _styleId);
          return !!find(data?.hybridArts, {id: hybridId});
        },
        (...args) => args.join(','),
      ),
    [data?.hybridArts],
  );

  const handleSelectStyle = useCallback(
    (id: string) => {
      setStyleId(id);
    },
    [setStyleId],
  );

  const handleSelectArtist = useCallback(
    (id: string) => {
      setArtistId(id);
    },
    [setArtistId],
  );

  const handleRunTransaction = useCallback(
    (eth) =>
      contract.startHybridWithEth(artistId, contentId, styleId, {
        value: eth,
      }),
    [artistId, contentId, contract, styleId],
  );

  const {art, player} = data || {};

  const areBothSelected = styleId && artistId;

  return (
    <>
      <h1>Create Hybrid</h1>
      <p>
        Create a new and unique NFT that's a combination of the 2 selected art works, generated via
        a custom-trained deep learning algorithm.
      </p>
      <Skeleton loading={loading} active={loading}>
        <Row gutter={50}>
          <Col xs={24} lg={9}>
            <h2>Choose Style</h2>
            <Row gutter={[16, 16]}>
              {player?.arts.map((a) => {
                const isExisting = isExistingHybrid(contentId, a.id);
                const isValid = !isExisting && contentId !== a.id;
                const invalidTooltip =
                  contentId === a.id
                    ? 'This art already selected as the Content art'
                    : 'This hybrid combination already exists';

                return (
                  <Col key={a.id} span={8}>
                    <Space direction='vertical' size='middle'>
                      <Art {...a} selected={styleId === a.id} size={ArtSize.Small} />
                      <ConditionalWrapper
                        wrap={!isValid}
                        wrapper={(children) => (
                          <Tooltip title={invalidTooltip}>{children}</Tooltip>
                        )}>
                        <Button
                          disabled={!isValid}
                          onClick={() => handleSelectStyle(a.id)}
                          size='small'>
                          Select
                        </Button>
                      </ConditionalWrapper>
                    </Space>
                  </Col>
                );
              })}
            </Row>
          </Col>

          <Col xs={24} lg={9}>
            <h2>Choose Artist</h2>
            <Row gutter={[16, 16]}>
              {player?.artists.map((a) => {
                const isBusy = a.creationReadyAt !== '0';
                return (
                  <Col key={a.id} span={6}>
                    <Space direction='vertical'>
                      <Artist {...a} selected={artistId === a.id} size={ArtistSize.Small} />
                      <ConditionalWrapper
                        wrap={isBusy}
                        wrapper={(children) => (
                          <Tooltip title='This artist is busy'>{children}</Tooltip>
                        )}>
                        <Button
                          disabled={isBusy}
                          onClick={() => handleSelectArtist(a.id)}
                          size='small'>
                          Select
                        </Button>
                      </ConditionalWrapper>
                    </Space>
                  </Col>
                );
              })}
            </Row>
          </Col>

          <Col xs={24} lg={6}>
            <h2>Content Art</h2>
            <Space direction='vertical' size='large' align='end'>
              <Art {...art} size={ArtSize.Small} />
              {hybridEth && (
                <ConditionalWrapper
                  wrap={!areBothSelected}
                  wrapper={(children) => (
                    <Tooltip
                      defaultVisible
                      placement='bottom'
                      title='Please select a Style and Artist'>
                      {children}
                    </Tooltip>
                  )}>
                  <TransactionButton
                    disabled={!areBothSelected}
                    type='primary'
                    size='large'
                    eth={hybridEth}
                    onRunTransaction={handleRunTransaction}
                    onReceipt={() => {
                      notification.success({
                        message: 'New hybrid started!',
                        description: 'This will take a few hours to complete.',
                      });
                      history.push('/');
                    }}
                    onFail={(_err, message) => {
                      notification.error({
                        message: 'Unable to start this hybrid',
                        description: message,
                      });
                    }}>
                    Start Hybrid
                  </TransactionButton>
                </ConditionalWrapper>
              )}
            </Space>
          </Col>
        </Row>
      </Skeleton>
    </>
  );
}
